import {useState} from 'react';
import {Alert, Button, CircularProgress, Tab, Tabs, TextField} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import {Link} from 'react-router-dom';
import {useReportPermissions} from '../hooks/use-report-permissions';
import {useReportList} from '../hooks/use-report-list';
import {useReportDownload} from '../hooks/use-report-download';
import {ReportRunOverrides, ReportTemplate} from '../types/report-builder.types';
import {hasReportRunFields} from '../helpers/report-run.helpers';
import {ReportTemplateCard} from './report-template-card';
import {DeleteReportDialog} from './delete-report-dialog';
import {ReportRunDialog} from './report-run-dialog';
import styles from '../report-pages.module.scss';

const ReportList = () => {
  const {canView, canManage, canDownload, companyId} = useReportPermissions();
  const [myOnly, setMyOnly] = useState(true);
  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState<ReportTemplate | null>(null);
  const [launching, setLaunching] = useState<ReportTemplate | null>(null);
  const list = useReportList(myOnly, canView, companyId);
  const download = useReportDownload();
  const visible = list.reports.filter((report) =>
    report.name.toLocaleLowerCase('ru').includes(search.trim().toLocaleLowerCase('ru')),
  );
  const generate = async (report: ReportTemplate, overrides?: ReportRunOverrides) => {
    const result = await download.run(report, overrides);
    if (result?.missingTemplate) list.refresh();
    if (result?.completed || result?.document || result?.missingTemplate) setLaunching(null);
  };
  const run = (report: ReportTemplate) => {
    if (download.busy || !canView || !canDownload) return;
    if (!download.getPendingDocument(report.id) && hasReportRunFields(report.body)) {
      download.clear();
      setLaunching(report);
    } else {
      void generate(report);
    }
  };

  return (
    <section className={styles.page} aria-labelledby="report-list-title">
      <Button component={Link} to="/reports" startIcon={<ArrowBackIcon />} sx={{px: 0, mb: 2, textTransform: 'none'}}>
        Назад к отчётам
      </Button>
      <header className={styles.heading}>
        <div>
          <h1 id="report-list-title">{myOnly ? 'Мои отчёты' : 'Все доступные отчёты'}</h1>
          <p>Создавайте, редактируйте и управляйте своими отчётами</p>
        </div>
        {canView && canManage && (
          <Button
            component={Link}
            to="/reports/builder/new"
            variant="contained"
            disableElevation
            startIcon={<AddIcon />}
            sx={{textTransform: 'none', flexShrink: 0}}
          >
            Создать отчёт
          </Button>
        )}
      </header>
      {!canView ? (
        <Alert severity="warning">У вас нет прав на просмотр шаблонов отчётов.</Alert>
      ) : (
        <>
          <div className={styles.listToolbar}>
            <Tabs
              value={myOnly ? 'mine' : 'all'}
              onChange={(_, value) => {
                setMyOnly(value === 'mine');
                download.clear();
              }}
              aria-label="Доступность отчётов"
            >
              <Tab value="mine" label="Мои" disabled={download.busy} sx={{textTransform: 'none'}} />
              <Tab value="all" label="Все доступные" disabled={download.busy} sx={{textTransform: 'none'}} />
            </Tabs>
            <TextField
              size="small"
              label="Поиск отчёта"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{width: {xs: '100%', sm: 260}, backgroundColor: '#fff'}}
            />
          </div>
          {!canDownload && (
            <Alert severity="info" sx={{mb: 2}}>
              У вас нет прав на скачивание файлов отчётов.
            </Alert>
          )}
          {download.state.error && !launching && (
            <Alert severity="error" onClose={download.clear} sx={{mb: 2}}>
              {download.state.error}
            </Alert>
          )}
          {download.state.completed && (
            <Alert severity="success" onClose={download.clear} sx={{mb: 2}}>
              Отчёт сформирован и передан на скачивание.
            </Alert>
          )}
          {download.busy && (
            <p className={styles.status} role="status">
              {download.state.phase === 'generating' ? 'Формируется отчёт…' : 'Скачивается файл отчёта…'}
            </p>
          )}
          {list.loading ? (
            <div className={styles.empty} role="status">
              <CircularProgress size={28} />
              <p>Загрузка отчётов…</p>
            </div>
          ) : list.error ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" onClick={list.refresh}>
                  Повторить
                </Button>
              }
            >
              {list.error}
            </Alert>
          ) : visible.length ? (
            <div className={styles.grid}>
              {visible.map((report) => (
                <ReportTemplateCard
                  key={report.id}
                  report={report}
                  editable={canManage && list.ownIds.has(report.id)}
                  downloadable={canDownload}
                  busy={download.busy}
                  download={
                    download.getPendingDocument(report.id)
                      ? {
                          ...download.state,
                          reportId: report.id,
                          document: download.getPendingDocument(report.id),
                          phase: download.state.reportId === report.id ? download.state.phase : undefined,
                        }
                      : download.state
                  }
                  onRun={run}
                  onDelete={setDeleting}
                />
              ))}
            </div>
          ) : (
            <div className={styles.empty}>
              <h2>{search.trim() ? 'Отчёты не найдены' : 'Здесь пока нет отчётов'}</h2>
              <p>
                {search.trim()
                  ? 'Попробуйте другое название.'
                  : canManage
                    ? 'Нажмите «Создать отчёт», чтобы выбрать поля и настроить свой первый отчёт.'
                    : 'Доступные вам шаблоны появятся здесь.'}
              </p>
            </div>
          )}
        </>
      )}
      {deleting && (
        <DeleteReportDialog
          report={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            download.clear();
            list.refresh();
          }}
        />
      )}
      {launching && (
        <ReportRunDialog
          report={launching}
          busy={download.busy}
          download={download.state}
          onClose={() => {
            setLaunching(null);
            download.clear();
          }}
          onRun={(overrides) => generate(launching, overrides)}
        />
      )}
    </section>
  );
};

export default ReportList;
